import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { ChartService } from '../chart.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ChartModule } from 'primeng/chart';
import { TabMenuModule } from 'primeng/tabmenu';
import { MenuItem, MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { Chart } from '../chart';
import { ButtonModule } from 'primeng/button';
import { ComboChart } from '../model/combo-chart';
import { DateTime } from 'luxon';
import { MonthsLong } from '@shared/enums/months-long';
import { Observable, Subscription, tap } from 'rxjs';
import { AppService } from 'app/app.service';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';

@Component({
  selector: 'app-combo-chart',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbComponent,
    ChartModule,
    TabMenuModule,
    ButtonModule
  ],
  templateUrl: './combo-chart.component.html',
  styleUrl: './combo-chart.component.scss'
})

export class ComboChartComponent implements OnInit, DoCheck, OnDestroy {

  documentStyle = getComputedStyle(document.documentElement);
  textColor = this.documentStyle.getPropertyValue('--text-primary');
  textColorSecondary = this.documentStyle.getPropertyValue('--text-color-secondary');
  surfaceBorder = this.documentStyle.getPropertyValue('--surface-border');


  chartData?: Chart;
  timeByMonth?: ComboChart[];
  options: any;
  lightModeOptions: any;
  darkModeOptions: any;

  tabItems?: MenuItem[];
  activeItem?: MenuItem;

  previousTheme?: string;

  periodTotal: number = 0;
  highestMonth: number = 0;
  periodAverage: number = 0;
  highestMonthLabel: string = '';
  activeYear: number = 0;
  btnDisabledIncrement: boolean = false;
  btnDisabledDecrement: boolean = false;

  theme$?: string;
  sub?: Subscription;

  constructor(
    public appService: AppService,
    private messageSerive: MessageService,
    private chartService: ChartService,
    private errorLogService: ErrorLogService
  ) { }

  chartResponseData$?: Observable<ComboChart[]>;
  
  ngOnInit(): void {

    this.sub = this.appService.theme$.subscribe((theme) => {
        this.theme$ = theme;
    });

    this.activeYear = DateTime.now().year;
    this.btnDisabledIncrement = this.activeYear === DateTime.now().year;

    this.chartResponseData$ = this.chartService.getByMonthLineChartCombo()
    .pipe(
      tap( (chartResponse) => {
        if (!chartResponse) {
          this.errorLogService.logError(`Error or no response from chart service`);
          this.messageSerive.add({severity:'error', summary: 'Error', detail: 'No chart data found'});
          this.loadEmptyChart();
          return;
        }
        this.timeByMonth = chartResponse;
        this.formatTimebyMonthBarChartData(this.activeYear);
        this.calculateTotals();
      })
    );

    this.lightModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          labels: {
            color: '#35454c'
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#6b7280'
          },
          grid: {
            color: ''
          }
        },
        y: {
          ticks: {
            color: '#6b7280'
          },
          grid: {
            color: '#eeecec'
          }
        }
      }
    };

    this.darkModeOptions = {
      maintainAspectRatio: false,
      responsive: false,
      aspectRatio: 0.6,
      plugins: {
        legend: {
          labels: {
            color: '#e7e7e7'
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: "#e7e7e7"
          },
          grid: {
            color: ''
          }
        },
        y: {
          ticks: {
            color: "#e7e7e7"
          },
          grid: {
            color: '#2b2b2b'
          },
          border: {
            color: '#2b2b2b'
          }
        }
      }
    };

    this.tabItems = [
      { label: 'Bar' },
      { label: 'Line' }
    ];

    this.activeItem = this.tabItems[0];
    this.options = this.lightModeOptions;

  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onActiveItemChange(event: MenuItem) {
    this.activeItem = event;
    if (event.label == 'Bar') {
      this.formatTimebyMonthBarChartData(this.activeYear);
    }
    else {
      this.formatTimebyMonthLineChartData(this.activeYear);
    }
  }

  onIncrementYear() {
    const currentYear = DateTime.now().year;
    this.activeYear += 1;
    this.btnDisabledIncrement = this.activeYear === currentYear ? true : false;

    if (this.activeItem?.label == 'Bar') {
      this.formatTimebyMonthBarChartData(this.activeYear);
      this.calculateTotals();
    }
    else {
    this.formatTimebyMonthLineChartData(this.activeYear);
    this.calculateTotals();
    }
  }

  onDecrementYear() {
    const currentYear = DateTime.now().year;
    this.activeYear -= 1;
    this.btnDisabledIncrement = this.activeYear === currentYear ? true : false;
    
    if (this.activeItem?.label == 'Bar') {
      this.formatTimebyMonthBarChartData(this.activeYear);
      this.calculateTotals();
    }
    else {
      this.formatTimebyMonthLineChartData(this.activeYear);
      this.calculateTotals();
    }
  }

  calculateTotals() {
    const filteredForYear = this.timeByMonth?.filter( (item) => item.year === this.activeYear)[0];
    if (filteredForYear) {
      this.periodTotal = filteredForYear.months.data.reduce((a, b) => a + b, 0);
      this.highestMonth = Math.max(...filteredForYear.months.data);
      this.periodAverage = this.periodTotal / 12;
      const highestMonthIndex = filteredForYear.months.data.indexOf(this.highestMonth);
      const highestMonthLabel = filteredForYear.months.label[highestMonthIndex];
      this.highestMonthLabel = MonthsLong[highestMonthLabel - 1];
    }
    else {
      this.periodTotal = 0;
      this.highestMonth = 0;
      this.periodAverage = 0;
      this.highestMonthLabel = '';
      return;
    }
  }

  formatTimebyMonthBarChartData(year: number) {

    const filteredForYear = this.timeByMonth?.filter( (item) => item.year === year)[0];
    if (filteredForYear) {
      if(this.theme$ === 'light') {
        this.chartData = {
          labels: this.getMonthNames(filteredForYear?.months.label || []),
          datasets: [
            {
              type: 'bar',
              label: 'Flight Time',
              data: filteredForYear.months.data || [],
              backgroundColor: '#1c80cf50',
              borderColor: '#1c80cf',
              borderWidth: 1,
            }
          ]
        };
      }
      else {
  
        this.chartData = {
          labels: this.getMonthNames(filteredForYear?.months.label || []),
          datasets: [
            {
              type: 'bar',
              label: 'Flight Time',
              data: filteredForYear.months.data || [],
              backgroundColor: '#75BEF850',
              borderColor: '#75BEF8',
              borderWidth: 1,
            }
          ]
        };
      }
    }
    else {
      this.loadEmptyChart();
      this.calculateTotals();
    }
  }

  formatTimebyMonthLineChartData(year: number) {
    const filteredForYear = this.timeByMonth?.filter( (item) => item.year === year)[0];
    if (filteredForYear) {
      if(this.theme$ === 'light') {
        this.chartData = {
          labels: this.getMonthNames(filteredForYear.months.label || []),
          datasets: [
            {
              label: 'Flight Time',
              fill: false,
              borderColor: '#1c80cf',
              yAxisID: 'y',
              tension: 0.4,
              data: filteredForYear?.months.data || []
            }
          ]
        }
      }
      else {
        this.chartData = {
          labels: this.getMonthNames(filteredForYear.months.label || []),
          datasets: [
            {
              label: 'Flight Time',
              fill: false,
              borderColor: '#75BEF8',
              yAxisID: 'y',
              tension: 0.4,
              data: filteredForYear?.months.data || []
            }
          ]
        }
      }
    }
    else {
      this.loadEmptyChart();
      this.calculateTotals();
    }
  }

  getMonthNames(months: number[]): string[] {
    return months.map( (month) =>  MonthsLong[month - 1] );
  }


  ngDoCheck(): void {
    const currentTheme = this.theme$;
    if (this.previousTheme !== currentTheme) {
      this.previousTheme = currentTheme;
      this.options = this.toggleThemeOptions();
      if (this.activeItem?.label == 'Bar') {
        if (this.timeByMonth) {
          this.formatTimebyMonthBarChartData(this.activeYear);
        }
        else {
          this.loadEmptyChart();
          this.calculateTotals();
        }
      }
      else {
        if (this.timeByMonth) {
          this.formatTimebyMonthLineChartData(this.activeYear);
        }
        else {
          this.loadEmptyChart();
          this.calculateTotals();
        }
      }
    }
  }

  toggleThemeOptions(): any {
    if (this.theme$ === 'light') {
      return this.lightModeOptions;
    } else {
      return this.darkModeOptions;
    }
  }

  loadEmptyChart(): void {
    if(this.theme$ === 'light') {
      this.chartData = {
        labels: this.chartService.emptyChartData.month,
        datasets: [
          {
            type: 'bar',
            label: 'Flight Time',
            data: this.chartService.emptyChartData.data,
            backgroundColor: '#1c80cf50',
            borderColor: '#1c80cf',
            borderWidth: 1,
          }
        ]
      };
    }
    else {
      this.chartData = {
        labels: this.chartService.emptyChartData.month,
        datasets: [
          {
            type: 'bar',
            label: 'Flight Time',
            data: this.chartService.emptyChartData.data,
            backgroundColor: '#75BEF850',
            borderColor: '#75BEF8',
            borderWidth: 1,
          }
        ]
      };
    }
  }

}
